import {
  LOAD_TEST_RESULT_START,
  LOAD_TEST_RESULT_SUCCESS,
  SET_TEST_RESULT_PROGRESS,
  SET_TEST_NODE_EXPANDED,
  SET_TEST_NODE_EXPAND_COLLAPSE_ALL,
  LOAD_TEST_RESULT_ERROR,
  RcaThunkDispatch,
} from "./actionTypes";
import { AnyAction } from "@reduxjs/toolkit";
import { ITestStatus, ResultFormat } from "../services/TestResultRepository";
import { IRcaTestResult } from "../models/IRcaTestResult";
import { Utils } from "../Utils";
import { IRcaState, LoadTestErrorReason } from "../store/IRcaState";
import { IServices } from "../services/IServices";

/**
 * Creates an action to indicate the start of test result loading
 *
 * @returns The action
 */
const loadTestResultStart = (): AnyAction => ({
  type: LOAD_TEST_RESULT_START,
});

/**
 * Creates an action to indicate the load of a test result completed
 *
 * @param result - The test result that was loaded
 * @param downloadXmlUrl - The result XML download URL
 * @param downloadHtmlUrl - The result HTML download URL
 * @returns The action
 */
const loadTestResultSuccess = (result: IRcaTestResult, downloadXmlUrl: string, downloadHtmlUrl: string): AnyAction => {
  let nodeKey = 0;
  return {
    type: LOAD_TEST_RESULT_SUCCESS,
    result: Utils.mapResult(result, () => ({ key: nodeKey++ })),
    downloadXmlUrl,
    downloadHtmlUrl,
  };
};

/**
 * Creates an action to indicate loading the test failed
 *
 * @param reason - The reason for the test load error
 * @returns The action
 */
const loadTestResultError = (reason: LoadTestErrorReason): AnyAction => ({
  type: LOAD_TEST_RESULT_ERROR,
  reason,
});

/**
 * Creates an action to update the status of a running test
 *
 * @param description - The running test description
 * @returns The action
 */
const setTestInProgressStatus = (description: string): AnyAction => ({
  type: SET_TEST_RESULT_PROGRESS,
  description,
});

/**
 * Creates an action that expands or collapses a test result node
 *
 * @param node - The node to expand
 * @param expanded - A value indicating whether the node should be expanded (true) or collapsed
 * @returns The action
 */
export const setTestNodeExpanded = (node: IRcaTestResult, expanded: boolean): AnyAction => ({
  type: SET_TEST_NODE_EXPANDED,
  node,
  expanded,
});

/**
 * Creates an action that expands or collapses all of the test result nodes
 *
 * @param expanded - A value indicating whether all nodes should be expanded (true) or collapsed
 * @returns The action
 */
export const setTestNodeExpandCollapseAll = (expanded = true): AnyAction => ({
  type: SET_TEST_NODE_EXPAND_COLLAPSE_ALL,
  expanded,
});

/**
 * Creates an action that loads a test result
 *
 * @param resultId - The result id to load
 * @param language - The language to render the result
 * @returns The thunk action
 */
export const loadTestResult = (resultId: string, language: string) => {
  return async (dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    dispatch(loadTestResultStart());

    let status: ITestStatus | null = null;

    // Wait 5 seconds between checks
    const delayMilliseconds = 5000;

    do {
      try {
        status = await services.testResultRepository.getTestStatusAsync(language, resultId);
        if (status.status === "Running" && status.statusDescription) {
          dispatch(setTestInProgressStatus(status.statusDescription));
          await Utils.delayAsync(delayMilliseconds);
        }
      } catch (error) {
        await Utils.delayAsync(delayMilliseconds);
      }
    } while (!status || status.status === "NotStarted" || status.status === "Running");

    try {
      const result = await services.testResultRepository.getTestResultAsync(language, resultId);
      if (!result) {
        // No result is "not found". Happens with incorrect test id or expired result
        dispatch(loadTestResultError("NotFound"));
      } else {
        const xmlUrl = services.testResultRepository.getDownloadUrl(language, resultId, result.DisplayFormType, ResultFormat.Xml);
        const htmlUrl = services.testResultRepository.getDownloadUrl(language, resultId, result.DisplayFormType, ResultFormat.Html);
        dispatch(loadTestResultSuccess(result, xmlUrl, htmlUrl));
      }
    } catch {
      dispatch(loadTestResultError("FailedHttp"));
    }
  };
};
